import { TREND_STYLES } from "@/pages/dashboard/data/mock-stats";
import type { DashboardMetric } from "@/pages/dashboard/types";

interface MetricCardProps {
  metric: DashboardMetric;
}

/**
 * A single stat card (icon + label + value + optional trend badge).
 * Mirrors the Figma "Metrics/Metrics-Card" pattern in node 833:16355.
 */
const MetricCard = ({ metric }: MetricCardProps) => {
  const { icon, label, value, trend } = metric;

  return (
    <div className="bg-white border border-[#00000017] rounded-[4px] p-5 flex flex-col gap-6 self-stretch min-w-px overflow-hidden">
      {/* Icon bubble */}
      <div className="size-10 bg-[#0000000B] rounded-[4px] flex items-center justify-center p-2 shrink-0">
        <img src={icon} alt="" aria-hidden="true" className="size-5" />
      </div>

      {/* Label + value + trend */}
      <div className="flex flex-col gap-4 w-full">
        <div className="flex flex-col gap-0.5 w-full">
          <p className="text-sm leading-5 tracking-[-0.14px] text-[#000000] w-full">
            {label}
          </p>
          <p className="font-georgia text-[44px] leading-[52px] tracking-[-1.32px] font-bold text-[#000000] w-full">
            {value}
          </p>
        </div>

        {trend ? <MetricTrendBadge trend={trend} /> : null}
      </div>
    </div>
  );
};

interface MetricTrendBadgeProps {
  trend: NonNullable<DashboardMetric["trend"]>;
}

const MetricTrendBadge = ({ trend }: MetricTrendBadgeProps) => {
  const style = TREND_STYLES[trend.direction];

  return (
    <div
      className={`inline-flex w-fit items-center gap-1 h-6 pl-2 pr-1 rounded ${style.classes}`}
    >
      <span className="text-sm leading-5 tracking-[-0.14px] whitespace-nowrap">
        {trend.label}
      </span>
      <img src={style.icon} alt="" aria-hidden="true" className="size-4 shrink-0" />
    </div>
  );
};

export default MetricCard;
